import { useState, useEffect, useCallback } from 'react'
import { fetchContentfulEntries } from '../lib/contentful'

export interface Project {
  id: string
  title: string
  slug: string
  category: string
  description: string
  tags: string[]
  cover_image: string
  client_name: string
  live_url: string
  featured: boolean
  published: boolean
  sort_order: number
}

// Contentful field shape for "projects" content type
interface ContentfulProjectFields {
  title: string
  slug: string
  description?: string | RichTextDocument
  clientName?: string
  projectImage?: { sys: { id: string } }
  serviceLink?: { sys: { id: string } }
  liveProjectUrl?: string
}

interface RichTextDocument {
  nodeType: string
  content: RichTextNode[]
  data: Record<string, any>
}

interface RichTextNode {
  nodeType: string
  value?: string
  content?: RichTextNode[]
  marks?: { type: string }[]
  data?: Record<string, any>
}

function extractPlainText(
  field: string | RichTextDocument | undefined,
): string {
  if (!field) return ''
  if (typeof field === 'string') return field
  if (!field.content) return ''

  function walkNodes(nodes: RichTextNode[]): string {
    return nodes
      .map((node) => {
        if (node.nodeType === 'text' && node.value) return node.value
        if (node.content) return walkNodes(node.content)
        return ''
      })
      .join('')
  }

  return field.content
    .map((block) => {
      if (block.content) return walkNodes(block.content)
      return ''
    })
    .join('\n\n')
}

const STORAGE_KEY = 'belo_projects'
const PROJECTS_UPDATED_EVENT = 'belo_projects_updated'

function getStoredProjects(): Project[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw)
  } catch {
    return []
  }
}

function saveProjects(projects: Project[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects))
}

function dispatchProjectsUpdate() {
  window.dispatchEvent(new Event(PROJECTS_UPDATED_EVENT))
}

export function addProject(data: Omit<Project, 'id' | 'sort_order'>): Project {
  const projects = getStoredProjects()
  const newProject: Project = {
    ...data,
    id: `proj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    sort_order: projects.length,
  }
  projects.push(newProject)
  saveProjects(projects)
  dispatchProjectsUpdate()
  return newProject
}

export function removeProject(id: string): void {
  const projects = getStoredProjects().filter((p) => p.id !== id)
  saveProjects(projects)
  dispatchProjectsUpdate()
}

export function updateProject(id: string, updates: Partial<Project>): void {
  const projects = getStoredProjects()
  const index = projects.findIndex((p) => p.id === id)
  if (index !== -1) {
    projects[index] = { ...projects[index], ...updates }
    saveProjects(projects)
    dispatchProjectsUpdate()
  }
}

function mapContentfulProject(
  entry: { sys: { id: string }; fields: ContentfulProjectFields },
  index: number,
): Project {
  return {
    id: entry.sys.id,
    title: entry.fields.title || '',
    slug: entry.fields.slug || '',
    category: '',
    description: extractPlainText(entry.fields.description),
    tags: [],
    cover_image: '',
    client_name: entry.fields.clientName || '',
    live_url: entry.fields.liveProjectUrl || '',
    featured: false,
    published: true,
    sort_order: index,
  }
}

export function useProjects() {
  const [allProjects, setAllProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchFromCMS = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const entries =
        await fetchContentfulEntries<ContentfulProjectFields>('projects')
      const mapped = entries.map(mapContentfulProject)
      setAllProjects(mapped)
      saveProjects(mapped)
    } catch (err) {
      console.error(
        'Failed to fetch projects from CMS, falling back to local:',
        err,
      )
      setError(true)
      setAllProjects(getStoredProjects())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFromCMS()
  }, [fetchFromCMS])

  useEffect(() => {
    const handler = () => setAllProjects(getStoredProjects())
    window.addEventListener(PROJECTS_UPDATED_EVENT, handler)
    return () => window.removeEventListener(PROJECTS_UPDATED_EVENT, handler)
  }, [])

  const projects = allProjects
    .filter((p) => p.published)
    .sort((a, b) => a.sort_order - b.sort_order)

  return { projects, allProjects, loading, error, refetch: fetchFromCMS }
}
